import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Checkbox, Dropdown, Flex, Input, Typography } from 'antd';

import { ArrowDownOutlined, ArrowUpOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import isEqual from 'fast-deep-equal';
import sortBy from 'lodash.sortby';

import { getTableConfigurations } from '../../../redux/actions/table-configurations';

import './styles.scss';

const { Text } = Typography;

function MultiSelectDropdown({ columns, tableId, handleReset, onChange }) {
  const dispatch = useDispatch();

  const userTablePreferences = useSelector(state => state.user.tablePreferences);
  const { configurations } = useSelector(state => state.tableConfigurations);

  const currentTablePreferences = userTablePreferences?.find(table => table.id === tableId);
  const originalTableConfigs = configurations?.find(table => table.id === tableId);

  const [isPreferencesChanged, setIsPreferencesChanged] = useState(false);
  const [isResetEnabled, setIsResetEnabled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState(columns);
  const [searchValue, setSearchValue] = useState('');

  const selectedCount = selectedValues.filter(column => !column.hidden).length;

  const handleSearchChange = e => {
    setSearchValue(e.target.value.toLowerCase());
  };

  function checkEquality() {
    const visibleColumns = originalTableConfigs?.columns?.filter(column => column.is_visible);

    return isEqual(
      sortBy(currentTablePreferences?.columns, ['is_visible', 'order']),
      sortBy(visibleColumns, ['is_visible', 'order'])
    );
  }

  useEffect(() => {
    dispatch(getTableConfigurations());

    const isUserPreferencesEqualToOriginalConfig = checkEquality();

    setIsResetEnabled(!isUserPreferencesEqualToOriginalConfig);

    setOptions(columns?.map(({ key, title }) => ({ value: key, label: title })));
  }, []);

  useEffect(() => {
    setSelectedValues(columns);

    const isUserPreferencesEqualToOriginalConfig = checkEquality();

    setIsResetEnabled(!isUserPreferencesEqualToOriginalConfig);
    setOptions(selectedValues?.map(({ key, title }) => ({ value: key, label: title })));
  }, [userTablePreferences, columns]);

  useEffect(() => {
    checkUserPreferencesChange();
    setOptions(selectedValues?.map(({ key, title }) => ({ value: key, label: title })));
  }, [selectedValues]);

  function checkUserPreferencesChange() {
    const normalize = arr => {
      if (!arr?.length) return;

      return sortBy(
        arr.map(({ order, hidden, id }) => ({ order, hidden, id })),
        JSON.stringify
      );
    };

    const isColumnSelectionUnchanged = isEqual(
      normalize(
        currentTablePreferences?.columns.map(column => ({ ...column, hidden: !column.is_visible, id: column.id }))
      ),
      normalize(
        selectedValues.map(column => ({ order: column.order, hidden: column.hidden, id: column.columnReferenceId }))
      )
    );

    setIsPreferencesChanged(!isColumnSelectionUnchanged);
  }

  const clearAll = () => {
    const updatedValues = selectedValues.map(item => ({ ...item, hidden: true }));
    setSelectedValues(updatedValues);
  };

  const selectAll = () => {
    const updatedValues = selectedValues.map(item => ({ ...item, hidden: false }));
    setSelectedValues(updatedValues);
  };

  const filteredOptions = options.filter(option => option.label.toLowerCase().includes(searchValue));

  const handleCheckboxChange = selectedOption => {
    const updatedValues = selectedValues.map(item => {
      if (item.key === selectedOption.value) {
        return { ...item, hidden: !item.hidden };
      }

      return item;
    });

    setSelectedValues(updatedValues);
  };

  const handleSave = () => {
    onChange?.(selectedValues);
  };

  const moveColumn = (index, direction) => {
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= options.length) return;

    const updatedOptions = [...selectedValues];

    [updatedOptions[index].order, updatedOptions[newIndex].order] = [
      updatedOptions[newIndex].order,
      updatedOptions[index].order
    ];

    updatedOptions.sort((a, b) => a.order - b.order);

    setSelectedValues(updatedOptions);
  };

  const menu = {
    items: [
      {
        key: 'search',
        label: (
          <div className="search-wrapper p-2">
            <Input
              placeholder="Search column"
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={handleSearchChange}
            />
          </div>
        ),
        disabled: true
      },
      {
        type: 'group',
        key: 'checkbox-group',
        label: (
          <div className="px-2 pb-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {filteredOptions.map((option, index) => (
              <Flex
                align="center"
                className={classnames('option-item', 'p-1', {
                  'selected-option': !selectedValues?.find(item => item.key === option.value)?.hidden !== false
                })}
                key={option.value}
                vertical={false}>
                <Checkbox
                  style={{ width: '100%' }}
                  checked={!selectedValues?.find(item => item.key === option.value)?.hidden !== false}
                  onChange={() => handleCheckboxChange(option)}>
                  {option.label}
                </Checkbox>
                <Flex align="center" gap="small" vertical={false}>
                  <Button
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => moveColumn(index, -1)}
                  />
                  <Button
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={index === options.length - 1}
                    onClick={() => moveColumn(index, 1)}
                  />
                </Flex>
              </Flex>
            ))}
          </div>
        )
      },
      {
        key: 'selectAll',
        label: (
          <Flex
            align="center"
            justify="space-between"
            className="p-2"
            vertical={false}
            style={{ borderTop: '1px solid #e9ecef', width: '100%' }}>
            <Flex
              justify={tableId ? 'start' : 'space-between'}
              style={{ width: tableId ? 'auto' : '100%' }}
              vertical={false}
              wrap="nowrap">
              <Button size="small" type="link" disabled={selectedValues.some(value => value.hidden)} onClick={clearAll}>
                Clear all
              </Button>
              <Button
                size="small"
                type="primary"
                disabled={selectedValues.every(value => !value.hidden)}
                onClick={selectAll}>
                Select all
              </Button>
            </Flex>
            {tableId && (
              <>
                <Flex vertical={false} wrap="nowrap">
                  <Button size="small" type="link" disabled={!isResetEnabled} onClick={handleReset}>
                    Reset
                  </Button>
                  <Button size="small" type="primary" disabled={!isPreferencesChanged} onClick={handleSave}>
                    Save
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        ),
        disabled: true
      }
    ]
  };

  return (
    <Dropdown
      overlayClassName="multi-select-dropdown"
      placement="bottomRight"
      open={isMenuOpen}
      menu={menu}
      onOpenChange={setIsMenuOpen}
      trigger={['click']}
      overlayStyle={{
        backgroundColor: 'white',
        maxHeight: 500,
        overflowY: 'auto',
        width: '268px',
        boxShadow: '0 5px 10px rgba(0, 0, 0, .5)'
      }}>
      <Button variant="outlined">
        {selectedValues.length > 0 ? (
          <>
            Visible columns{' '}
            <Text type="secondary">
              ({selectedCount}/{options?.length})
            </Text>
          </>
        ) : (
          'Select Options'
        )}
        <DownOutlined style={{ marginLeft: '8px' }} />
      </Button>
    </Dropdown>
  );
}

export default memo(MultiSelectDropdown);
